import React, { useEffect, useState } from "react";
import {
  X, FileClock, Search, Shield, Download, FileSpreadsheet, FileText,
  SlidersHorizontal, ArrowRight, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { api, API, getToken } from "@/lib/api";
import {
  ENTITY_LABELS, ACTION_LABELS, ACTION_TONES,
  DEFAULT_PERMISSION_LABELS_SHORT, DEFAULT_TAB_LABELS, ROLE_LABELS,
  formatDate, toLocalInput, toIsoFromLocal,
} from "./userUtils";
import { DateInput } from "@/components/DateInput";

const PAGE_SIZE = 50;

export default function AuditLogModal({ onClose, labels }) {
  const permShortLabels = labels?.permissionsShort || DEFAULT_PERMISSION_LABELS_SHORT;
  const tabLabels = labels?.tabs || DEFAULT_TAB_LABELS;
  const roleLabels = labels?.roles || ROLE_LABELS;
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [entity, setEntity] = useState("");
  const [action, setAction] = useState("");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [exporting, setExporting] = useState("");
  const [loadingMore, setLoadingMore] = useState(false);
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 7); d.setHours(0, 0, 0, 0);
    return toLocalInput(d);
  });
  const [dateTo, setDateTo] = useState(() => toLocalInput(new Date()));

  const isPermsView = entity === "role_permissions";

  const buildParams = (offset) => {
    const p = new URLSearchParams();
    if (entity) p.set("entity", entity);
    if (action) p.set("action", action);
    if (dateFrom) p.set("date_from", toIsoFromLocal(dateFrom));
    if (dateTo) p.set("date_to", toIsoFromLocal(dateTo, true));
    p.set("offset", String(offset));
    p.set("limit", String(PAGE_SIZE));
    return p;
  };

  const load = async () => {
    setRows(null);
    try {
      const r = await api.get(`/audit-logs?${buildParams(0).toString()}`);
      setRows(r.data.rows || []);
      setTotal(r.data.total || 0);
    } catch {
      setRows([]); setTotal(0);
      toast.error("Denetim kaydı yüklenemedi");
    }
  };

  const loadMore = async () => {
    if (!rows) return;
    setLoadingMore(true);
    try {
      const r = await api.get(`/audit-logs?${buildParams(rows.length).toString()}`);
      setRows([...rows, ...(r.data.rows || [])]);
      setTotal(r.data.total || 0);
    } catch {
      toast.error("Daha fazla kayıt yüklenemedi");
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity, action, dateFrom, dateTo]);

  const handleExport = async (fmt) => {
    setExporting(fmt);
    try {
      const p = new URLSearchParams();
      if (entity) p.set("entity", entity);
      if (action) p.set("action", action);
      if (dateFrom) p.set("date_from", toIsoFromLocal(dateFrom));
      if (dateTo) p.set("date_to", toIsoFromLocal(dateTo, true));
      p.set("format", fmt);
      const token = getToken();
      const res = await fetch(`${API}/audit-logs/export?${p.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("export failed");
      let fname = `denetim_kaydi_${new Date().toISOString().slice(0, 10)}.${fmt}`;
      const cd = res.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="?([^"]+)"?/i);
      if (m && m[1]) fname = m[1];
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(fmt === "xlsx" ? "Excel indirildi" : "CSV indirildi");
    } catch {
      toast.error("Dışa aktarım başarısız");
    } finally {
      setExporting("");
    }
  };

  const filtered = (rows || []).filter((r) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return [r.label, r.actor_email, r.entity_id].some((v) => String(v || "").toLowerCase().includes(q));
  });

  const canLoadMore = rows && rows.length < total;

  // Build summary for perms-only view: count flag flips per role/flag
  const permsSummary = isPermsView ? buildPermsSummary(filtered) : null;

  const permLabel = (flag) => (flag === "tabs" ? "Erişilebilir sekmeler" : (permShortLabels[flag] || flag));
  const tabLabel = (tab) => tabLabels[tab] || tab;
  const roleLabel = (role) => roleLabels[role] || role;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-2 sm:p-4" data-testid="audit-log-modal">
      <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-5xl max-h-[92vh] flex flex-col">
        <div className="p-4 sm:p-5 border-b border-slate-200 flex items-center justify-between gap-3">
          <div>
            <h3 className="font-display text-lg font-bold flex items-center gap-2">
              <FileClock size={18} /> Denetim Kaydı
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">Tüm CRUD işlemleri · kim, ne zaman, ne yaptı</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 grid place-items-center" data-testid="audit-close-button">
            <X size={16} />
          </button>
        </div>

        <div className="p-4 border-b border-slate-200 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[180px]">
            <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="audit-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Etiket, email veya ID ara..."
              className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-xs"
            />
          </div>
          <select data-testid="audit-entity-filter" value={entity} onChange={(e) => setEntity(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-lg text-xs font-semibold">
            <option value="">Tüm Varlıklar</option>
            {Object.entries(ENTITY_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
          <button
            data-testid="audit-filter-perms-shortcut"
            onClick={() => setEntity(entity === "role_permissions" ? "" : "role_permissions")}
            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold uppercase tracking-wider border transition-colors ${
              isPermsView ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-indigo-700 border-indigo-200 hover:bg-indigo-50"
            }`}
            title="Yalnızca Rol İzni değişikliklerini göster"
          >
            <Shield size={12} /> Rol İzin Değişiklikleri
          </button>
          {(() => {
            // "Son 7 Gün Silmeler" quick filter — toggles action=delete + date_from=7d ago
            const sevenDaysAgo = (() => {
              const d = new Date();
              d.setDate(d.getDate() - 6);
              return d.toISOString().slice(0, 10);
            })();
            const isDeletesView = action === "delete" && dateFrom === sevenDaysAgo && !dateTo;
            const toggle = () => {
              if (isDeletesView) {
                setAction("");
                setDateFrom("");
              } else {
                setAction("delete");
                setDateFrom(sevenDaysAgo);
                setDateTo("");
              }
            };
            return (
              <button
                data-testid="audit-filter-recent-deletes"
                onClick={toggle}
                className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold uppercase tracking-wider border transition-colors ${
                  isDeletesView ? "bg-rose-600 text-white border-rose-600" : "bg-white text-rose-700 border-rose-200 hover:bg-rose-50"
                }`}
                title="Son 7 günde kim neyi silmiş · hızlı filtre"
              >
                <Trash2 size={12} /> Son 7 Gün Silmeler
              </button>
            );
          })()}
          <select data-testid="audit-action-filter" value={action} onChange={(e) => setAction(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-lg text-xs font-semibold">
            <option value="">Tüm Aksiyonlar</option>
            {Object.entries(ACTION_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>

        <div className="px-4 pb-3 pt-1 border-b border-slate-200 flex flex-wrap items-center gap-2 text-xs">
          <div className="inline-flex items-center gap-1.5">
            <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Başlangıç</label>
            <DateInput data-testid="audit-date-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="px-2 py-1.5 border border-slate-200 rounded-lg text-xs font-mono-tab" />
          </div>
          <div className="inline-flex items-center gap-1.5">
            <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Bitiş</label>
            <DateInput data-testid="audit-date-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="px-2 py-1.5 border border-slate-200 rounded-lg text-xs font-mono-tab" />
          </div>
          <button data-testid="audit-date-clear" onClick={() => { setDateFrom(""); setDateTo(""); }} className="px-2 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-[11px] font-semibold">
            Tarih Temizle
          </button>
          <div className="ml-auto flex items-center gap-2">
            <button data-testid="audit-export-xlsx" onClick={() => handleExport("xlsx")} disabled={!!exporting} className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider disabled:opacity-50">
              <FileSpreadsheet size={12} /> {exporting === "xlsx" ? "..." : "Excel"}
            </button>
            <button data-testid="audit-export-csv" onClick={() => handleExport("csv")} disabled={!!exporting} className="inline-flex items-center gap-1.5 bg-slate-800 hover:bg-slate-900 text-white rounded-lg px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider disabled:opacity-50">
              <FileText size={12} /> {exporting === "csv" ? "..." : "CSV"}
            </button>
          </div>
        </div>

        {isPermsView && permsSummary && permsSummary.rows.length > 0 && (
          <div className="px-4 py-3 border-b border-slate-200 bg-indigo-50/40" data-testid="audit-perms-summary">
            <div className="flex items-center gap-2 mb-2">
              <SlidersHorizontal size={13} className="text-indigo-700" />
              <span className="text-[11px] font-bold uppercase tracking-wider text-indigo-800">Flag Değişiklik Özeti</span>
              <span className="text-[11px] text-indigo-500 font-mono-tab">{permsSummary.total} toplam değişiklik</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {permsSummary.rows.map((s) => (
                <div
                  key={`${s.role}-${s.flag}`}
                  data-testid={`audit-perms-chip-${s.role}-${s.flag}`}
                  className="inline-flex items-center gap-1.5 bg-white border border-indigo-200 rounded-full pl-2 pr-2.5 py-1 text-[11px]"
                  title={`${ROLE_LABELS[s.role] || s.role} · ${permLabel(s.flag)}: ${s.on} açma, ${s.off} kapama`}
                >
                  <span className="font-bold text-indigo-800">{roleLabel(s.role)}</span>
                  <span className="text-slate-500">·</span>
                  <span className="font-semibold text-slate-800 max-w-[180px] truncate">{permLabel(s.flag)}</span>
                  {s.on > 0 && <span className="inline-flex items-center gap-0.5 bg-emerald-100 text-emerald-700 rounded px-1 font-bold">▲{s.on}</span>}
                  {s.off > 0 && <span className="inline-flex items-center gap-0.5 bg-rose-100 text-rose-700 rounded px-1 font-bold">▼{s.off}</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="overflow-y-auto flex-1" data-testid="audit-rows">
          {rows === null && <div className="py-16 text-center text-slate-400 text-sm">Yükleniyor...</div>}
          {rows && filtered.length === 0 && (
            <div className="py-16 text-center text-slate-400 text-sm">Kayıt bulunamadı</div>
          )}
          {rows && filtered.length > 0 && (
            <div className="divide-y divide-slate-100">
              {filtered.map((r) => {
                const isOpen = expanded === r.id;
                const isPermsRow = r.entity === "role_permissions";
                return (
                  <div key={r.id} data-testid={`audit-row-${r.id}`} className="px-4 sm:px-5 py-3">
                    <button
                      onClick={() => setExpanded(isOpen ? null : r.id)}
                      className="w-full text-left flex flex-wrap items-center gap-2 hover:bg-slate-50 rounded-lg -mx-2 px-2 py-1.5"
                    >
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${ACTION_TONES[r.action] || "bg-slate-100 text-slate-700"}`}>
                        {ACTION_LABELS[r.action] || r.action}
                      </span>
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${isPermsRow ? "bg-indigo-100 text-indigo-700" : "bg-slate-100 text-slate-700"}`}>
                        {ENTITY_LABELS[r.entity] || r.entity}
                      </span>
                      <span className="font-semibold text-sm text-slate-900 truncate flex-1 min-w-[120px]">
                        {r.label || r.entity_id || "—"}
                      </span>
                      <span className="font-mono-tab text-[11px] text-slate-500">{formatDate(r.at)}</span>
                    </button>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-slate-500 pl-1">
                      <span>
                        <span className="text-slate-400">Yapan:</span>{" "}
                        <span className="font-semibold text-slate-700">{r.actor_email || "sistem"}</span>
                        {r.actor_role && <span className="ml-1 opacity-70">({r.actor_role})</span>}
                      </span>
                      {r.impersonated_by && (
                        <span className="inline-flex items-center gap-1 bg-rose-50 text-rose-700 rounded px-1.5 py-0.5 font-semibold">
                          <Shield size={10} /> {r.impersonated_by.email} impersonation ile
                        </span>
                      )}
                      {r.ip && <span className="font-mono-tab text-slate-400">{r.ip}</span>}
                    </div>
                    {isOpen && (isPermsRow ? (
                      <PermsRowDiff before={r.before} after={r.after} entityId={r.entity_id} permLabel={permLabel} tabLabel={tabLabel} />
                    ) : (
                      <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-[11px]">
                        <div className="bg-rose-50 border border-rose-100 rounded-lg p-2 overflow-auto max-h-56">
                          <div className="font-bold text-rose-800 mb-1">ÖNCE</div>
                          <pre className="whitespace-pre-wrap font-mono-tab text-rose-900">{r.before ? JSON.stringify(r.before, null, 2) : "—"}</pre>
                        </div>
                        <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-2 overflow-auto max-h-56">
                          <div className="font-bold text-emerald-800 mb-1">SONRA</div>
                          <pre className="whitespace-pre-wrap font-mono-tab text-emerald-900">{r.after ? JSON.stringify(r.after, null, 2) : "—"}</pre>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })}
              {canLoadMore && (
                <div className="p-4 text-center">
                  <button
                    data-testid="audit-load-more"
                    onClick={loadMore}
                    disabled={loadingMore}
                    className="inline-flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg px-4 py-2 text-xs font-semibold disabled:opacity-50"
                  >
                    <Download size={12} /> {loadingMore ? "Yükleniyor..." : `Daha fazla yükle (${total - rows.length} kayıt daha)`}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="p-3 border-t border-slate-200 text-center text-[11px] text-slate-400">
          {rows ? `${filtered.length} / ${total} kayıt gösteriliyor` : ""}
        </div>
      </div>
    </div>
  );
}

// Pretty-print a permissions change entry: show per-flag chip with Kapalı → Açık style
function PermsRowDiff({ before, after, entityId, permLabel, tabLabel }) {
  // Granular entries store {flag: value} in before/after
  const b = before || {};
  const a = after || {};
  const keys = Array.from(new Set([...Object.keys(b), ...Object.keys(a)]));
  if (keys.length === 0) {
    return (
      <div className="mt-2 bg-indigo-50/50 border border-indigo-100 rounded-lg p-3 text-[11px] text-slate-500 italic" data-testid={`audit-perms-diff-${entityId || "all"}`}>
        Değişiklik detayı yok (muhtemelen değişiklik yok çağrısı).
      </div>
    );
  }
  return (
    <div className="mt-2 bg-indigo-50/50 border border-indigo-100 rounded-lg p-3" data-testid={`audit-perms-diff-${entityId || "all"}`}>
      <div className="text-[10px] font-bold uppercase tracking-wider text-indigo-800 mb-2">Flag Diff</div>
      <div className="space-y-1.5">
        {keys.map((k) => (
          <div key={k} className="flex items-center flex-wrap gap-2 text-[11px]">
            <span className="font-bold text-slate-800 min-w-[180px]">{permLabel(k)}</span>
            <span className="inline-flex items-center gap-1">
              <ValueChip value={b[k]} flag={k} tone="before" tabLabel={tabLabel} />
              <ArrowRight size={10} className="text-slate-400" />
              <ValueChip value={a[k]} flag={k} tone="after" tabLabel={tabLabel} />
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ValueChip({ value, flag, tone, tabLabel }) {
  const toneCls = tone === "before" ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700";
  let txt;
  if (flag === "tabs") {
    const list = Array.isArray(value) ? value : [];
    txt = list.length ? list.map((t) => tabLabel(t)).join(", ") : "yok";
  } else if (value === true) txt = "Açık";
  else if (value === false) txt = "Kapalı";
  else if (value === undefined) txt = "—";
  else txt = String(value);
  return <span className={`inline-flex px-2 py-0.5 rounded font-bold ${toneCls}`}>{txt}</span>;
}

function buildPermsSummary(rows) {
  const byKey = new Map(); // key: role|flag
  let total = 0;
  for (const r of rows) {
    if (r.entity !== "role_permissions") continue;
    const b = r.before || {};
    const a = r.after || {};
    for (const flag of Object.keys({ ...b, ...a })) {
      const role = r.entity_id || "all";
      const key = `${role}|${flag}`;
      if (!byKey.has(key)) byKey.set(key, { role, flag, on: 0, off: 0 });
      const entry = byKey.get(key);
      const bVal = b[flag];
      const aVal = a[flag];
      if (flag === "tabs") {
        // "on" if more tabs added than removed; counts a single change
        total += 1;
        entry.on += 1;
      } else if (bVal === false && aVal === true) {
        entry.on += 1; total += 1;
      } else if (bVal === true && aVal === false) {
        entry.off += 1; total += 1;
      }
    }
  }
  const list = Array.from(byKey.values()).sort((x, y) => (y.on + y.off) - (x.on + x.off));
  return { rows: list, total };
}
